// Routing controls for a queue pick: who receives it, approve, decline.
//
// Opener and reason textareas belonged to Scout. The one judgement here is who
// receives the account. Changing the dropdown and approving writes that email
// onto the pick; apply_approved → assign_account is still the only ownership path.

import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { approveQueue, declineQueue, panelMessage } from '../api/client';
import type {
  ApprovalPick, ApprovalQueuePayload, BookAccount, DeclinedPick, StrategistOption,
  WriteActionResult,
} from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { EMPTY_QUEUE_STRIP } from '../lib/emptyStates.ts';
import { writeOutcomeLine, writeOutcomeTone } from '../lib/writeOutcome.ts';
import { approveAnywayLine, isFlaggedRequest } from './bankRequest.ts';
import { strategistLabel } from './queueSignal.ts';
import { queueOwnerStart } from './strategistOwners.ts';
import { DECLINE_REASON_PLACEHOLDER, declinedQueueLine } from './requestReason.ts';

export function BankHealthStrip({ line }: { line: string }) {
  return <p className="bankhealth">{line || EMPTY_QUEUE_STRIP}</p>;
}

export function useQueueWrite(
  onQueueChange: (next: ApprovalQueuePayload) => void,
) {
  const { getToken } = useAuthContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<WriteActionResult[]>([]);

  async function run(send: () => Promise<ApprovalQueuePayload>) {
    setBusy(true);
    setError(null);
    try {
      const next = await send();
      onQueueChange(next);
      if (next.results) setResults(next.results);
    } catch (caught) {
      setError(panelMessage(caught, 'The queue could not be updated.'));
    } finally {
      setBusy(false);
    }
  }

  return {
    busy,
    error,
    results,
    approve(
      accountId: string,
      ownerEmail: string,
      requestedBy?: string | null,
      originalAccountId?: string,
      overrideExclusion?: boolean,
    ) {
      const original = originalAccountId || accountId;
      const owners: Record<string, string> = { [accountId]: ownerEmail, [original]: ownerEmail };
      const requesters = requestedBy
        ? { [accountId]: requestedBy, [original]: requestedBy }
        : undefined;
      void run(() => approveQueue(getToken, {
        account_ids: [accountId],
        owners,
        requesters,
        // A flagged pick is only approved after the "Approve anyway" confirmation, so that
        // click is the force; the server still refuses never-contact.
        override_exclusion: Boolean(overrideExclusion),
      }));
    },
    decline(accountId: string, reason: string, requestedBy?: string | null) {
      void run(() => declineQueue(getToken, {
        account_ids: [accountId],
        reason,
        requesters: requestedBy ? { [accountId]: requestedBy } : undefined,
      }));
    },
  };
}

// Renders the whole queue row: the caller's leading cells, then the route cells. It owns
// the <tr> so a flagged pick's "Approve anyway" confirm can open as a full-width strip
// on its own row underneath, instead of wrapping inside the narrow action column.
export function QueueRouteControls({
  pick,
  strategists,
  canApprove,
  busy,
  onApprove,
  onDecline,
  rowClassName,
  columnCount,
  onRowClick,
  onRowKeyDown,
  children,
}: {
  rowClassName: string;
  // The row opens the account; the table decides how.
  onRowClick?: (event: MouseEvent<HTMLElement>) => void;
  onRowKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  columnCount: number;
  children: ReactNode;
  pick: ApprovalPick;
  strategists: StrategistOption[];
  canApprove: boolean;
  busy: boolean;
  onApprove: (
    accountId: string,
    ownerEmail: string,
    requestedBy?: string | null,
    originalAccountId?: string,
    overrideExclusion?: boolean,
  ) => void;
  onDecline: (accountId: string, reason: string, requestedBy?: string | null) => void;
}) {
  // Only the listed strategists: a proposed owner who is not one (the bank, a requester) is not
  // offered, and the approver chooses instead.
  const candidates = pick.candidate_accounts || [];
  const [owner, setOwner] = useState(queueOwnerStart(pick.owner, strategists));
  const [chosenId, setChosenId] = useState(pick.account_id);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [confirmingFlag, setConfirmingFlag] = useState(false);
  const flagged = isFlaggedRequest(pick);
  const anywayLine = approveAnywayLine(pick);

  function approve() {
    if (flagged && !confirmingFlag) {
      setConfirmingFlag(true);
      return;
    }
    onApprove(chosenId, owner, pick.requested_by, pick.account_id, flagged && confirmingFlag);
  }

  const showConfirm = canApprove && !declining && confirmingFlag;

  return (
    <>
      <tr className={rowClassName} tabIndex={onRowClick ? 0 : undefined} onClick={onRowClick} onKeyDown={onRowKeyDown}>
        {children}
        <td>
          <div className="qstrategist">
          {canApprove ? (
            <>
              {candidates.length > 1 ? (
                <select
                  aria-label={`Salesforce account for ${pick.account}`}
                  value={chosenId}
                  disabled={busy}
                  onChange={event => setChosenId(event.target.value)}
                >
                  {candidates.map(candidate => (
                    <option key={candidate.account_id} value={candidate.account_id}>
                      {candidate.name}
                    </option>
                  ))}
                </select>
              ) : null}
              <select
                aria-label={`Strategist for ${pick.account}`}
                value={owner}
                disabled={busy}
                onChange={event => setOwner(event.target.value)}
              >
                {owner ? null : <option value="">Choose a strategist…</option>}
                {strategists.map(row => (
                  <option key={row.email} value={row.email}>
                    {row.name}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <span className="sdrchip">{strategistLabel(pick.owner, strategists)}</span>
          )}
          {pick.retirement_note ? <span className="ovflag" title={pick.retirement_note}>Note</span> : null}
          </div>
        </td>
        <td>
          {canApprove ? (
            declining ? null : (
              // While the confirm strip is open below, its buttons are the only ones:
              // the row's own Approve and Decline step aside rather than stack.
              showConfirm ? null : (
                <div className="qactions">
                  <button
                    type="button"
                    className="btn primary"
                    disabled={busy || !owner}
                    onClick={approve}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={() => setDeclining(true)}
                  >
                    Decline
                  </button>
                </div>
              )
            )
          ) : null}
        </td>
      </tr>
      {/* The decline reason gets a row of its own, like the confirm strip, so the pick's row
          stays one line. */}
      {canApprove && declining ? (
        <tr className={rowClassName + ' qconfirmrow'}>
          <td colSpan={columnCount}>
            <div className="qdecline">
              <input
                type="text"
                placeholder={DECLINE_REASON_PLACEHOLDER}
                aria-label={`Reason to decline ${pick.account} (optional)`}
                value={declineReason}
                disabled={busy}
                autoFocus
                onChange={event => setDeclineReason(event.target.value)}
              />
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => onDecline(pick.account_id, declineReason.trim(), pick.requested_by)}
              >
                Confirm decline
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => {
                  setDeclining(false);
                  setDeclineReason('');
                }}
              >
                Cancel
              </button>
            </div>
          </td>
        </tr>
      ) : null}
      {showConfirm ? (
        <tr className={rowClassName + ' qconfirmrow'}>
          <td colSpan={columnCount}>
            <div className="qconfirm">
              <p className="qconfirmline">{anywayLine}</p>
              <div className="qactions">
                <button
                  type="button"
                  className="btn primary"
                  disabled={busy || !owner}
                  onClick={approve}
                >
                  Approve anyway
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => setConfirmingFlag(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function BankRouteControls({
  account,
  strategists,
  canApprove,
  busy,
  onRoute,
}: {
  account: BookAccount;
  strategists: StrategistOption[];
  canApprove: boolean;
  busy: boolean;
  onRoute: (account: BookAccount, ownerEmail: string) => void;
}) {
  const [owner, setOwner] = useState('');

  return (
    <>
      <td>
        {canApprove ? (
          <select
            aria-label={`Strategist for ${account.account}`}
            value={owner}
            disabled={busy}
            onChange={event => setOwner(event.target.value)}
          >
            <option value="">Choose…</option>
            {strategists.map(row => (
              <option key={row.email} value={row.email}>
                {row.name}
              </option>
            ))}
          </select>
        ) : (
          <span>—</span>
        )}
      </td>
      <td>
        {canApprove ? (
          <div className="qactions">
            <button
              type="button"
              className="btn primary"
              disabled={busy || !owner || !account.domain}
              onClick={() => onRoute(account, owner)}
            >
              {busy ? 'Routing…' : 'Route'}
            </button>
          </div>
        ) : null}
      </td>
    </>
  );
}

export function QueueOutcome({
  declined,
  results,
  error,
}: {
  declined: DeclinedPick[];
  results: WriteActionResult[];
  error: string | null;
}) {
  return (
    <>
      {declined.length > 0 ? (
        <div className="qdeclined">
          {declined.map(item => (
            <p key={`${item.account_id}-${item.declined_at}`} className="briefwhy">
              {declinedQueueLine(item.account || item.domain || item.account_id, item.declined_by, item.declined_reason)}
            </p>
          ))}
        </div>
      ) : null}
      {results.map((result, index) => (
        <p key={index} className={'actionnote ' + writeOutcomeTone(result)}>
          {writeOutcomeLine(result, 'Assign')}
        </p>
      ))}
      {error ? <p className="actionnote bad">{error}</p> : null}
    </>
  );
}
